import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Flex,
  Heading,
  useColorModeValue,
  Button,
  useDisclosure,
  Text,
  useToast, // added
} from '@chakra-ui/react';
import TableSkeleton from '../configure/components/tableSkeleton';
import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls';
import { useLocation } from 'react-router-dom';
import Card from 'components/card/Card';
import InterfaceSelect from 'components/select';
import ThresholdModal from './modal/ThresholdModal';
import ComplexTable from '../default/components/ComplexTable';

const Thresholds = () => {
  const location = useLocation();
  const deviceId = localStorage.getItem('deviceId');
  const textColor = useColorModeValue('navy.700', 'white');
  const cardBg = useColorModeValue('white', 'navy.700');
  const [loading, setLoading] = useState(true);
  const [configurations, setConfigurations] = useState([]);
  const [selectedModbusConfigId, setSelectedModbusConfigId] = useState('');
  const [modbusConfigMap, setModbusConfigMap] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [selectedParam, setSelectedParam] = useState(null);
  const [maxThreshold, setMaxThreshold] = useState('');
  const [minThreshold, setMinThreshold] = useState('');

  const fetchInterfaces = useCallback(async () => {
    try {
      const keys = 'modbus_configs';
      const resp = await axiosInstance.get(
        apiUrls.groupAttributes(deviceId, keys),
      );
      const attrMap = Object.fromEntries(
        resp.data.map((a) => [a.key, a.value]),
      );
      const configMap = (attrMap.modbus_configs || []).reduce((acc, i) => {
        const id = i.id || '';
        const name = i.name || '';
        acc[name] = id;
        return acc;
      }, {});
      setModbusConfigMap(configMap);
      if (
        !selectedModbusConfigId &&
        (attrMap.modbus_configs || []).length > 0
      ) {
        setSelectedModbusConfigId(attrMap.modbus_configs[0].id);
      }
    } catch (err) {}
  }, [deviceId, selectedModbusConfigId]);

  // const fetchModbusConfigurations = useCallback(async () => {
  //   if (!deviceId || !selectedModbusConfigId) return;
  //   try {
  //     const resp = await axiosInstance.get(
  //       apiUrls.getModbusData(selectedModbusConfigId),
  //     );
  //     const attrMap = Object.fromEntries(
  //       resp.data.map((a) => [a.key, a.value]),
  //     );
  //     const modbusConfig = attrMap['modbus_config'];
  //     if (!modbusConfig?.slave_list?.length) {
  //       setConfigurations([]);
  //       setLoading(false);
  //       return;
  //     }
  //     const modbusID = modbusConfig.id;
  //     const slaveUUIDs = modbusConfig.slave_list.map((s) => s.uuid).join(',');
  //     // Pass both modbusID and slaveUUIDs as keys (comma-separated)
  //     const keys = [modbusID, slaveUUIDs].filter(Boolean).join(',');
  //     const metaResp = await axiosInstance.get(
  //       apiUrls.toolDeviceAttributes(deviceId, keys),
  //     );
  //     const metaMap = Object.fromEntries(
  //       metaResp.data.map((a) => [a.key, a.value]),
  //     );
  //     const mergedConfigurations = modbusConfig.slave_list.map((slave) => {
  //       const config = slave.config || {};
  //       const meta = metaMap[slave.uuid] || {};
  //       return {
  //         id: slave.uuid,
  //         name: meta.name || '',
  //         slaveid: config.slaveid || '',
  //         slaveip: config.slaveip || '',
  //         interface_id: config.interface_id ?? 0,
  //         parameters: (meta.parameters || []).map((param) => ({
  //           ...param,
  //           id: param.id,

  //         })),
  //       };
  //     });
  //     setConfigurations(mergedConfigurations);
  //   } catch (err) {
  //     setConfigurations([]);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [deviceId, selectedModbusConfigId]);

  const fetchModbusConfigurations = useCallback(async () => {
    if (!deviceId || !selectedModbusConfigId) return;
    try {
      const resp = await axiosInstance.get(
        apiUrls.getModbusData(selectedModbusConfigId),
      );
      const attrMap = Object.fromEntries(
        resp.data.map((a) => [a.key, a.value]),
      );
      const modbusConfig = attrMap['modbus_config'];
      if (!modbusConfig?.slave_list?.length) {
        setConfigurations([]);
        setLoading(false);
        return;
      }
      const newid = modbusConfig.slave_list.map((s) => s.uuid).join(',');
      const keys = `${newid}&modbus_id=${selectedModbusConfigId}`;
      const metaResp = await axiosInstance.get(
        apiUrls.toolDeviceAttributes(deviceId, keys),
      );
      const metaMap = Object.fromEntries(
        metaResp.data.map((a) => [a.key, a.value]),
      );
      const mergedConfigurations = modbusConfig.slave_list.map((slave) => {
        const config = slave.config || {};
        const meta = metaMap[slave.uuid] || {};
        return {
          id: slave.uuid,
          name: meta.name || '',
          slaveid: config.slaveid || '',
          slaveip: config.slaveip || '',
          interface_id: config.interface_id ?? 0,
          parameters: (meta.parameters || []).map((param) => ({
            ...param,
            id: param.id,
          })),
        };
      });
      setConfigurations(mergedConfigurations);
    } catch (err) {
      setConfigurations([]);
    } finally {
      setLoading(false);
    }
  }, [deviceId, selectedModbusConfigId]);
  useEffect(() => {
    fetchInterfaces();
  }, [fetchInterfaces]);

  useEffect(() => {
    fetchModbusConfigurations();
  }, [fetchModbusConfigurations]);

  const ParamTypes = {
    '32bit float': 3,
    '32bit uint': 2,
    '32bit int': 18,
    Str: 42,
    '16bit float': 26,
    '16bit int': 14,
    '16bit uint': 1,
    Bool: 43,
  };
  const RegisterDataType = {
    DCBA: 1,
    BADC: 3,
    Bool: 2,
  };
  const writeFunctionCodes = {
    'Read Only': -1,
    'Write Coil': 5,
    '6 Label': 6,
    '16 Label': 16,
  };
  const Actions = {
    'Read Only': 0,
    'Read Write': 1,
  };

  const handleAddThreshold = async () => {
    if (!selectedParam) return;

    try {
      // Get current slave data
      const currentSlave = configurations.find(
        (s) => s.id === selectedParam.slave.id,
      );
      if (!currentSlave) return;

      // console.log('Selected param name:', selectedParam.param.name);
      // console.log('All parameters before update:', currentSlave.parameters);

      // Update ONLY the selected parameter using NAME as the identifier
      const updatedParameters = currentSlave.parameters.map((param) => {
        // Use name to identify the parameter since id doesn't exist
        if (param.name === selectedParam.param.name) {
          // console.log('Updating parameter:', param.name);
          return {
            ...param,
            thresholds: [
              {
                max: maxThreshold,
                min: minThreshold,
              },
            ],
          };
        }
        // Keep all other parameters unchanged
        // console.log(
        //   'Keeping parameter unchanged:',
        //   param.name,
        //   'thresholds:',
        //   param.thresholds,
        // );
        return { ...param };
      });

      // console.log(
      //   'Payload being sent:',
      //   JSON.stringify({ parameters: updatedParameters }, null, 2),
      // );

      const config = {
        parameters: updatedParameters,
      };

      const uploadId = selectedParam.slave.id;
      await axiosInstance.post(apiUrls.uploadThresholds(uploadId), config);

      setLoading(true);
      await fetchModbusConfigurations();

      toast({
        title: 'Threshold updated',
        description: 'Threshold updated successfully.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Upload error:', err);
      toast({
        title: 'Error',
        description: 'Failed to update threshold.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      onClose();
      // Clear the state
      setSelectedParam(null);
      setMaxThreshold('');
      setMinThreshold('');
    }
  };

  const createParametersColumns = (slave) => [
    {
      header: 'Name',
      accessorKey: 'name',
    },
    {
      header: 'Fn Code',
      accessorKey: 'fn_code',
    },
    {
      header: 'Address',
      accessorKey: 'address',
    },
    {
      id: 'type',
      header: 'Type',
      accessorFn: (param) =>
        Object.keys(ParamTypes).find((k) => ParamTypes[k] === param.type) ||
        param.type,
    },
    {
      header: 'Factor',
      accessorKey: 'factor',
    },
    {
      header: 'Reg Count',
      accessorKey: 'register_count',
    },
    {
      id: 'registerDataType',
      header: 'Register Data Type',
      accessorFn: (param) =>
        Object.keys(RegisterDataType).find(
          (k) => RegisterDataType[k] === param.register_data_type,
        ) || param.register_data_type,
    },
    {
      header: 'Offset',
      accessorKey: 'offset',
    },
    {
      id: 'writeFnCode',
      header: 'Write Fn',
      accessorFn: (param) =>
        Object.keys(writeFunctionCodes).find(
          (k) => writeFunctionCodes[k] === param.write_fn_code,
        ) || param.write_fn_code,
    },
    {
      id: 'access',
      header: 'Access',
      accessorFn: (param) =>
        Object.keys(Actions).find((k) => Actions[k] === (param.access || 0)) ||
        param.access ||
        0,
    },
    {
      id: 'minThreshold',
      header: 'Min Threshold',
      accessorFn: (param) => {
        const t = Array.isArray(param.thresholds)
          ? param.thresholds[0]
          : undefined;
        return t?.min ?? '';
      },
    },
    {
      id: 'maxThreshold',
      header: 'Max Threshold',
      accessorFn: (param) => {
        const t = Array.isArray(param.thresholds)
          ? param.thresholds[0]
          : undefined;
        return t?.max ?? '';
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          borderWidth="2px"
          borderColor="blue.200"
          color="blue.400"
          cursor="pointer"
          onClick={() => {
            // Prefill modal inputs from existing thresholds if present
            const existing = Array.isArray(row.original.thresholds)
              ? row.original.thresholds[0]
              : undefined;
            setSelectedParam({ slave, param: row.original });
            setMaxThreshold(existing?.max ?? '');
            setMinThreshold(existing?.min ?? '');
            onOpen();
          }}
        >
          Add Threshold
        </Button>
      ),
    },
  ];

  return (
    <>
      {loading ? (
        <TableSkeleton rowCount={5} columnCount={12} />
      ) : (
        <Box color={textColor}>
          <Flex justify="space-between" align="center" mt={1}>
            <Heading size="lg" mb={1}>
              Threshold Configuration
            </Heading>
            {Object.keys(modbusConfigMap).length > 0 && (
              <Box>
                <Flex>
                  <span
                    style={{ marginRight: 8, fontWeight: 600, marginTop: 4 }}
                  >
                    Config:
                  </span>
                  <InterfaceSelect
                    value={selectedModbusConfigId}
                    options={modbusConfigMap}
                    onChange={setSelectedModbusConfigId}
                  />
                </Flex>
              </Box>
            )}
          </Flex>
          {configurations.map((slave) => (
            <Box key={slave.id}>
              <Card bg={cardBg} p={4} my={4} color={textColor}>
                <Box mb={4}>
                  <Flex align="center" mb={2}>
                    <Text fontWeight="bold" mr={2}>
                      Name:
                    </Text>
                    <Text>{slave.name}</Text>
                  </Flex>
                  <Flex align="center">
                    <Text fontWeight="bold" mr={2}>
                      Slave ID:
                    </Text>
                    <Text>{slave.slaveid}</Text>
                  </Flex>
                </Box>
              </Card>
              {/* Parameters Table */}
              {slave.parameters && slave.parameters.length > 0 && (
                <ComplexTable
                  tableData={slave.parameters}
                  columnsData={createParametersColumns(slave)}
                  loading={false}
                  hasNext={false}
                  title={`Parameters for ${slave.name}`}
                  enableLocalPagination={true} // Enable local pagination
                  pageSize={10} // Optional: Set custom page size (default is 10)
                />
              )}
            </Box>
          ))}
        </Box>
      )}
      <ThresholdModal
        isOpen={isOpen}
        onClose={onClose}
        selectedParam={selectedParam}
        maxThreshold={maxThreshold}
        minThreshold={minThreshold}
        setMaxThreshold={setMaxThreshold}
        setMinThreshold={setMinThreshold}
        onAdd={handleAddThreshold}
      />
    </>
  );
};

export default Thresholds;
